import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Spinner,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import {
  useGetCrmSupplierContactQuotaByIdApi,
  useUpdateCrmSupplierContactQuotaApi,
} from 'apis/crm-supplier-contact-quotas';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface EditQuotaModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotaId: number;
}

interface FormData {
  supplierId: number;
  quota: number;
  remaining: number;
}

export const EditQuotaModal: React.FC<EditQuotaModalProps> = ({ isOpen, onClose, quotaId }) => {
  const toast = useCustomToast();

  // Fetch quota details
  const { data: quotaDetails, isLoading: isLoadingDetails } = useGetCrmSupplierContactQuotaByIdApi(quotaId, {
    enabled: isOpen && !!quotaId,
  });

  // Update mutation
  const { mutate: updateQuota, isLoading: isUpdating } = useUpdateCrmSupplierContactQuotaApi({
    onSuccess: () => {
      toast({
        title: 'Quota Updated Successfully!',
        description: 'Supplier contact quota has been updated.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onCloseModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Update Failed',
        description: error?.response?.data?.message || 'Failed to update quota. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const quotaValue = watch('quota');

  // Reset form when modal opens or quota details are loaded
  useEffect(() => {
    if (isOpen && quotaDetails) {
      reset({
        supplierId: quotaDetails.supplierId,
        quota: quotaDetails.quota,
        remaining: quotaDetails.remaining,
      });
    }
  }, [isOpen, quotaDetails, reset]);

  // Memoized close handler
  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // Memoized submit handler
  const onSubmit = useCallback(
    (data: FormData) => {
      if (data.remaining > data.quota) {
        toast({
          title: 'Invalid Data',
          description: 'Remaining quota cannot exceed total quota.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const payload: any = {};

      if (data.supplierId !== quotaDetails?.supplierId) {
        payload.supplierId = data.supplierId;
      }
      if (data.quota !== quotaDetails?.quota) {
        payload.quota = data.quota;
      }
      if (data.remaining !== quotaDetails?.remaining) {
        payload.remaining = data.remaining;
      }

      updateQuota({ id: quotaId, data: payload });
    },
    [updateQuota, quotaId, quotaDetails, toast]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Supplier Contact Quota</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isLoadingDetails ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Spinner size='lg' />
              <div style={{ marginTop: '16px', color: 'gray' }}>Loading quota details...</div>
            </div>
          ) : (
            <VStack spacing={4} align='stretch'>
              <FormControl isInvalid={!!errors.supplierId} isRequired>
                <FormLabel>Supplier ID</FormLabel>
                <Input
                  {...register('supplierId', {
                    required: 'Supplier ID is required',
                    min: { value: 1, message: 'Supplier ID must be positive' },
                    valueAsNumber: true,
                  })}
                  type='number'
                  placeholder='Enter supplier ID'
                />
                <FormErrorMessage>{errors.supplierId?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.quota} isRequired>
                <FormLabel>Total Quota</FormLabel>
                <NumberInput min={0}>
                  <NumberInputField
                    {...register('quota', {
                      required: 'Total quota is required',
                      min: { value: 0, message: 'Quota must be non-negative' },
                      valueAsNumber: true,
                    })}
                    placeholder='Enter total quota'
                  />
                </NumberInput>
                <FormErrorMessage>{errors.quota?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.remaining} isRequired>
                <FormLabel>Remaining Quota</FormLabel>
                <NumberInput min={0} max={quotaValue || undefined}>
                  <NumberInputField
                    {...register('remaining', {
                      required: 'Remaining quota is required',
                      min: { value: 0, message: 'Remaining quota must be non-negative' },
                      max: { value: quotaValue, message: 'Remaining quota cannot exceed total quota' },
                      valueAsNumber: true,
                    })}
                    placeholder='Enter remaining quota'
                  />
                </NumberInput>
                <FormErrorMessage>{errors.remaining?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='outline' onClick={onCloseModal} isDisabled={isUpdating}>
              Cancel
            </Button>
            <Button
              colorScheme='blue'
              onClick={handleSubmit(onSubmit)}
              isLoading={isUpdating}
              loadingText='Updating...'
              isDisabled={isLoadingDetails}>
              Update Quota
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
